/** German date/time formatting. The API sends UTC; the browser renders local time. */

import { format, formatDistanceToNow, isThisYear, isToday, isTomorrow, isYesterday } from 'date-fns'
import { de as deLocale } from 'date-fns/locale'

import { de } from '../i18n/de'

export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** `14.08.2026` — the year is dropped for dates in the current year. */
export function formatDate(value: string | null | undefined, withYear = true): string {
  const date = parseDate(value)
  if (!date) return '–'
  if (!withYear && isThisYear(date)) return format(date, 'dd.MM.', { locale: deLocale })
  return format(date, 'dd.MM.yyyy', { locale: deLocale })
}

export function formatDateTime(value: string | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '–'
  return format(date, 'dd.MM.yyyy, HH:mm', { locale: deLocale })
}

export function formatTime(value: string | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '–'
  return format(date, 'HH:mm', { locale: deLocale })
}

/** `vor 2 Stunden`, `gerade eben`. */
export function formatRelative(value: string | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '–'
  return formatDistanceToNow(date, { addSuffix: true, locale: deLocale })
}

/**
 * A delivery window as a person would say it.
 *
 * A date-only window covers the whole local day (see the backend's
 * `parse_german_day_window`), so it renders as a plain date rather than "00:00 – 23:59".
 */
export function formatDeliveryWindow(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  const start = parseDate(from)
  const end = parseDate(to)
  if (!start && !end) return '–'
  // The backend never stores a window that runs backwards; should one arrive anyway, the
  // end is the later statement (a deadline), so say that rather than "20.09. – 16.09.".
  if (start && end && start > end) return formatDeliveryWindow(null, to)
  const anchor = start ?? end!
  const label = dayLabel(anchor)

  if (!start || !end) return label

  const sameDay = start.toDateString() === end.toDateString()
  const wholeDay =
    start.getHours() === 0 && start.getMinutes() === 0 && end.getHours() === 23
  // DHL Paket names only the day, as a single point at local midnight. That is not a
  // delivery at 00:00 -- say the day, not "14.09., 00:00".
  const midnightPoint =
    start.getTime() === end.getTime() && start.getHours() === 0 && start.getMinutes() === 0

  if ((sameDay && wholeDay) || midnightPoint) return label
  // A single point in time rather than a window — say it once, not "20:10–20:10".
  if (start.getTime() === end.getTime()) return `${label}, ${formatTime(from)}`
  if (sameDay) return `${label}, ${formatTime(from)}–${formatTime(to)}`
  return `${label} – ${dayLabel(end)}`
}

/** `Heute`, `Morgen`, `Gestern`, otherwise `Mo, 21.09.` (with the year outside this one). */
export function dayLabel(date: Date): string {
  if (isToday(date)) return de.time.today
  if (isTomorrow(date)) return de.time.tomorrow
  if (isYesterday(date)) return de.time.yesterday
  return format(date, isThisYear(date) ? 'EEEEEE, dd.MM.' : 'EEEEEE, dd.MM.yyyy', {
    locale: deLocale,
  })
}

/** A past moment as a person reads it: `Heute, 14:32`, `Fr, 12.09., 16:05`. */
export function formatMoment(value: string | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '–'
  return `${dayLabel(date)}, ${format(date, 'HH:mm', { locale: deLocale })}`
}

/** Groups of four for readability, without changing the value that gets copied. */
export function formatTrackingNumber(value: string): string {
  if (value.length <= 8) return value
  return value.replace(/(.{4})/g, '$1 ').trim()
}
